import React, { Fragment, useEffect } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { GenericTable } from '../../components/_EOS/Table'
import { Courier } from '../../interfaces/Courier'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import { ApiConfig } from '../../config/api'
import axios from 'axios'
import { Modal } from '../../components/_EOS/Modal'
import { useModal } from '../../hooks/useModal'
import { usePagination } from '../../hooks/usePagination'
import { CourierForm } from './form/CourierForm'
import { CourierFormEdit } from './form'
import { Status } from '../../hooks/useFetchState'
import { CourierSearch } from './search/CourierSearch'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useSnackbar } from '../../hooks/useSnackbar'
import { Typography } from '@mui/material'
import { useTypesPriority } from '../../hooks/useTypesPriority'

interface CourierPageProps {}

export interface CourierSearchState {
  selected: string
  value: string
}

const getCouriers = (
  searchState: CourierSearchState = { selected: '', value: '' },
  page = 0,
  pageSize = 5
) => {
  const url = new URL(`${ApiConfig.CourierApi}`)

  url.searchParams.set('pageIndex', page.toString())

  url.searchParams.set('pageSize', pageSize.toString())

  if (searchState.selected && searchState.value) {
    url.searchParams.set(searchState.selected, searchState.value)
  }

  return axios.get(url.toString())
}

const deleteCourier = (courierId: number) => {
  const url = new URL(`${ApiConfig.CourierApi}/${courierId}`)

  return axios.delete(url.toString())
}

export const CourierPage: React.FC<CourierPageProps> = () => {
  const { state, callApi } = useApi<ApiResponse<Courier>>()
  const { state: deleteCourierState, callApi: deleteCourierCallApi } =
    useApi<ApiResponse<Courier>>()

  const [searchState, setSearchState] = React.useState({
    selected: '0',
    value: ''
  })

  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  const editModal = useModal<{ courierId: number }>('Editar courier')
  const createModal = useModal('Crear courier')
  const deleteModal = useModal<{ courier: Courier }>('Eliminar courier')

  const pagination = usePagination(5)

  const requestCouriers = (
    searchState?: CourierSearchState,
    page?: number,
    pageSize?: number
  ) => {
    callApi(getCouriers(searchState, page, pageSize)).catch((err) =>
      openSnackBar(err, false)
    )
  }

  const requestRemoveCourier = (courierId: number) => {
    deleteCourierCallApi(deleteCourier(courierId))
      .then(() => openSnackBar('Courier eliminado correctamente', true))
      .catch((err) => openSnackBar(err, false))
      .finally(() => {
        deleteModal.closeModal()
        requestCouriers(searchState, pagination.page, pagination.pageSize)
      })
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    // detectar si cambio la cantidad de filas por página
    // si lo hizo, vamos a la página 0
    if (pageSize === pagination.pageSize) {
      requestCouriers(searchState, page, pageSize)
      pagination.setPagination(page, pageSize)
    } else {
      requestCouriers(searchState, 0, pageSize)
      pagination.setPagination(0, pageSize)
    }
  }

  const closeCreateCourierModal = (success?: boolean) => {
    if (success) {
      createModal.closeModal()
      requestCouriers(undefined, pagination.page, pagination.pageSize)
    }
  }

  const handleSearch = () => {
    requestCouriers(searchState, 0, 5)
    pagination.reset()
  }

  const handleDeleteModal = deleteModal.extendedProps?.courier
    ? () => {
        requestRemoveCourier(deleteModal.extendedProps?.courier.courierId || 0)
      }
    : () => {}

  const {
    getTypesPriority,
    typesPriorityState: { typesPriority }
  } = useTypesPriority()

  useEffect(() => {
    if (!typesPriority || typesPriority?.length <= 0) {
      getTypesPriority()
    }
  }, [])

  return (
    <Fragment>
      <StandardPage
        pageTitle='Administrador de Couriers'
        buttonAction={() => createModal.openModal('md')}
        buttonTitle='Crear Courier'
      >
        <CourierSearch
          searchState={searchState}
          setSearchState={setSearchState}
          onSearchClick={handleSearch}
        />
        <GenericTable<Courier>
          headersConfig={[
            { field: 'courierId', title: 'ID' },
            { field: 'name', title: 'Nombre' }
          ]}
          IDKey={'courierId'}
          data={state.data?.items}
          actions={{
            onEdit: (id) => editModal.openModal('lg', { courierId: id }),
            onDelete: (_id, courier) => deleteModal.openModal('sm', { courier })
          }}
          pagination={{
            totalItem: state.data?.totalItems || 0,
            page: pagination.page,
            pageSize: pagination.pageSize
          }}
          onPaginationChange={handlePaginationChange}
          isLoading={state.status === Status.Loading}
        />
      </StandardPage>

      <Modal
        open={editModal.isOpen}
        maxWidth={editModal.size}
        onClose={editModal.closeModal}
        title={editModal.title}
      >
        {editModal.extendedProps && (
          <CourierFormEdit
            courierId={editModal.extendedProps?.courierId}
            handleSnackbar={openSnackBar}
          />
        )}
      </Modal>
      <Modal
        open={createModal.isOpen}
        maxWidth={createModal.size}
        onClose={createModal.closeModal}
        title={createModal.title}
      >
        <CourierForm
          onCreate={closeCreateCourierModal}
          handleSnackbar={openSnackBar}
        />
      </Modal>
      <Modal
        open={deleteModal.isOpen}
        maxWidth={deleteModal.size}
        onClose={deleteModal.closeModal}
        title={deleteModal.title}
        loading={deleteCourierState.status === Status.Loading}
        cancelButton={{
          action: () => deleteModal.closeModal(),
          title: 'Cancelar'
        }}
        successButton={{
          action: handleDeleteModal,
          title: 'Eliminar'
        }}
      >
        <Typography variant='body2' component={'h4'}>
          ¿Está seguro que desea eliminar el courier{' '}
          {deleteModal.extendedProps?.courier.name}?
        </Typography>
      </Modal>

      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}
